import React, { useContext, useState, useEffect } from "react";
import { supabase } from '../../../services/api/client';
import { ROLES } from "../../../constants";
import { isPassengerVip } from '../../DELIVERY/services/deliveryServices';
import {
  getAuthProfileById,
  callGetUserRoles,
  callGetUserMaxLevel,
} from '../../../services/api/account';

export const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const AUTH_CACHE_KEY = 'bryemma_auth_state_v1';

const readAuthCache = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.userId && Array.isArray(parsed.userRoles)) {
      return parsed;
    }
  } catch {}
  return null;
};

const writeAuthCache = (data) => {
  if (typeof window === 'undefined') return;
  try {
    if (!data || !data.userId) {
      localStorage.removeItem(AUTH_CACHE_KEY);
    } else {
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({
        userId: data.userId,
        user: data.user || null,
        profile: data.profile || null,
        userRoles: data.userRoles || [],
        maxRoleLevel: typeof data.maxRoleLevel === 'number' ? data.maxRoleLevel : 0,
        isVip: Boolean(data.isVip),
        updatedAt: Date.now(),
      }));
    }
  } catch {}
};

export function AuthProvider({ children }) {
  const initialCache = React.useMemo(() => readAuthCache(), []);

  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(() => initialCache?.profile || null);
  // Un perfil fallback (metadata de signup) puede estar desactualizado
  // (ej: admin promovido en BD cuya metadata dice 'passenger'): solo un
  // perfil cargado de BD aporta nivel — el fallback aporta 0.
  const [profileIsFromDb, setProfileIsFromDb] = useState(() => Boolean(initialCache?.profile));
  const [userRoles, setUserRoles] = useState(() => initialCache?.userRoles || []);
  const [maxRoleLevel, setMaxRoleLevel] = useState(() => initialCache?.maxRoleLevel || 0);
  const [loading, setLoading] = useState(true);
  const [isVip, setIsVip] = useState(() => initialCache?.isVip || false);
  // DEADLOCK supabase-js 2.105.x (gotrue-js#762): el callback de
  // onAuthStateChange corre con el navigator lock tomado; hacer await de
  // queries ahi lo bloquea todo (roles en timeout + logout colgado).
  // Diferimos el usuario hacia un effect (fuera del lock).
  const pendingUserRef = React.useRef(null);

  const withTimeout = (promise, ms = 4000) => 
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('RPC timeout')), ms))
    ]);

  const loadRoles = async (user) => {
    if (!user) {
      setUserRoles([]);
      setMaxRoleLevel(0);
      return { roles: [], level: 0 };
    }
    // Cada RPC aplica apenas llega con tope 4000ms sin bloquear al otro:
    // si un RPC tarda o falla, el otro aplica inmediatamente.
    try {
      const rolesPromise = withTimeout(callGetUserRoles(user.id), 4000)
        .then((res) => {
          const fetchedRoles = res?.data || null;
          if (fetchedRoles) setUserRoles(fetchedRoles);
          return fetchedRoles;
        })
        .catch(() => null);

      const levelPromise = withTimeout(callGetUserMaxLevel(user.id), 4000)
        .then((res) => {
          const fetchedLevel = typeof res?.data === 'number' ? res.data : null;
          if (fetchedLevel !== null) setMaxRoleLevel(fetchedLevel);
          return fetchedLevel;
        })
        .catch(() => null);

      const [roles, level] = await Promise.all([rolesPromise, levelPromise]);
      return { roles, level };
    } catch {
      return { roles: null, level: null };
    }
  };

  const loadProfile = async (user) => {
    if (!user) {
      setProfile(null);
      setProfileIsFromDb(false);
      return null;
    }
    try {
      const { data } = await withTimeout(
        getAuthProfileById(user.id),
        6000
      );
      if (data) {
        setProfileIsFromDb(true);
        setProfile(data);
        return data;
      }
      const fallback = { id: user.id, name: user.user_metadata?.name, email: user.email, role: user.user_metadata?.role || 'passenger' };
      setProfile(fallback);
      return fallback;
    } catch {
      const fallback = { id: user.id, name: user.user_metadata?.name, email: user.email, role: user.user_metadata?.role || 'passenger' };
      setProfile(prev => prev || fallback);
      return fallback;
    }
  };

  useEffect(() => {
    let mounted = true;
    let resolved = false;
    let activeLoadPromise = null;

    const hasOAuthCallback = () => {
      if (typeof window === 'undefined') return false;
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      return (
        hash.includes('access_token=') ||
        hash.includes('refresh_token=') ||
        hash.includes('error=') ||
        search.includes('code=') ||
        search.includes('error=')
      );
    };

    const timeout = setTimeout(() => {
      if (!mounted || resolved) return;
      // Timeout de seguridad: nunca cierra sesión ni degrada roles, solo libera
      // el bloqueo de render si la red externa está completamente colgada.
      setLoading(false);
    }, hasOAuthCallback() ? 10000 : 5000);

    const resolve = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeout);
      if (mounted) setLoading(false);
    };

    const loadUserData = (user) => {
      if (!user || !mounted) return Promise.resolve();
      if (activeLoadPromise) return activeLoadPromise;

      activeLoadPromise = (async () => {
        try {
          setCurrentUser(user);
          
          const fallbackProfile = {
            id: user.id,
            name: user.user_metadata?.name || user.email?.split('@')[0],
            email: user.email,
            role: user.user_metadata?.role || 'passenger'
          };
          setProfile(prev => prev || fallbackProfile);

          // Run profile, roles and VIP concurrently
          const [profData, roleData, vipStatus] = await Promise.all([
            loadProfile(user),
            loadRoles(user),
            isPassengerVip(user.id).then(vip => {
              if (mounted) setIsVip(vip);
              return vip;
            }).catch(() => {
              if (mounted) setIsVip(false);
              return false;
            })
          ]);

          // Si el RPC de nivel falló transitoriamente pero el perfil de BD confirma super_admin o admin,
          // preservamos el nivel administrativo para evitar degradación temporal a nivel 0.
          let resolvedLevel = typeof roleData?.level === 'number' ? roleData.level : null;
          if (resolvedLevel === null && profData) {
            if (profData.role === 'super_admin') resolvedLevel = 5;
            else if (profData.role === 'admin') resolvedLevel = 4;
          }
          if (resolvedLevel !== null && mounted) {
            setMaxRoleLevel(resolvedLevel);
          }

          // Persistir en cache autenticado para hidratación instantánea en recargas (0ms)
          writeAuthCache({
            userId: user.id,
            user: user,
            profile: profData || fallbackProfile,
            userRoles: roleData?.roles || [],
            maxRoleLevel: resolvedLevel !== null ? resolvedLevel : 0,
            isVip: vipStatus,
          });
        } finally {
          activeLoadPromise = null;
        }
      })();

      return activeLoadPromise;
    };

    const clearAll = () => {
      // Un drain diferido (TOKEN_REFRESHED) que dispare tras SIGNED_OUT
      // no debe revivir la sesion: se cancela el pendiente.
      pendingUserRef.current = null;
      activeLoadPromise = null;
      setCurrentUser(null);
      setProfile(null);
      setUserRoles([]);
      setMaxRoleLevel(0);
      setIsVip(false);
      writeAuthCache(null);
    };

    // 1) Restaurar sesión desde localStorage al montar
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      const user = session?.user || null;
      if (user) {
        await loadUserData(user);
        resolve();
      } else if (!hasOAuthCallback()) {
        setCurrentUser(null);
        resolve();
      }
      // Si !user pero hasOAuthCallback() es true: NO hacemos setCurrentUser(null) ni resolve(),
      // dejamos que onAuthStateChange procese el intercambio OAuth o que actúe el timeout de seguridad.
    }).catch(() => {
      if (mounted && !hasOAuthCallback()) {
        setCurrentUser(null);
        resolve();
      }
    });

    // DEADLOCK supabase-js 2.105.x (gotrue-js#762): onAuthStateChange invoca
    // este callback CON el navigator lock tomado y hace await de su retorno.
    // Si el callback espera queries (cada RPC pasa por getSession ->
    // _acquireLock), el lock se espera a si mismo: roles mueren por timeout
    // (super admin degradado a cliente) y signOut() queda colgado. Fix:
    // el callback solo captura el usuario y difiere el trabajo async con
    // setTimeout(0) — el lock se libera inmediatamente.
    const drainPending = () => {
      setTimeout(async () => {
        const user = pendingUserRef.current;
        pendingUserRef.current = null;
        if (user && mounted) {
          await loadUserData(user);
        }
        resolve();
      }, 0);
    };

    // 2) Escuchar cambios de estado (login, logout, refresh, OAuth)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      if (_event === 'SIGNED_OUT') {
        clearAll();
        resolve();
        return;
      }

      if (_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED' || _event === 'INITIAL_SESSION') {
        const user = session?.user || null;
        if (user) {
          pendingUserRef.current = user;
          drainPending();
        } else if (_event === 'INITIAL_SESSION') {
          const isErrorCallback = typeof window !== 'undefined' && (
            (window.location.hash || '').includes('error=') ||
            (window.location.search || '').includes('error=')
          );
          if (!hasOAuthCallback() || isErrorCallback) {
            resolve();
          }
        }
      }

      // Limpiar hash y parámetros OAuth de la URL sin recargar
      if (typeof window !== 'undefined') {
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        const isError = hash.includes('error') || search.includes('error');

        // CRÍTICO: 'code' solo debe limpiarse cuando Supabase ya confirmó SIGNED_IN
        // o INITIAL_SESSION con user (intercambio PKCE completado). Limpiarlo en
        // INITIAL_SESSION sin user borra el código antes de que Gotrue lo procese
        // y rompe el login de Google.
        if (_event === 'SIGNED_IN' || Boolean(session?.user) || isError) {
          const hasAuthHash = hash.includes('access_token') || hash.includes('refresh_token') || hash.includes('error');
          const searchParams = new URLSearchParams(search);
          const hasAuthSearch = searchParams.has('code') || searchParams.has('error');

          if (hasAuthHash || hasAuthSearch) {
            searchParams.delete('code');
            searchParams.delete('error');
            searchParams.delete('error_description');
            const cleanSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
            window.history.replaceState({}, document.title, window.location.pathname + cleanSearch);
          }
        }
      }
    });

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  // ── RBAC unificado (SPEC-003, BUG-006): user_roles es la UNICA fuente ──
  // El RPC v9x retorna TODOS los roles con is_active. Solo los ACTIVOS
  // otorgan permisos. profiles.role es cache derivada de BD: JAMAS se usa
  // para autorizar (los fallbacks anteriores permitian que un driver
  // pendiente operara como conductor y que un admin degradado a cliente
  // en timeouts fuera mal enrutado).
  const activeRoles = userRoles.filter(r => r.is_active !== false);
  const hasActiveRole = (names) => activeRoles.some(r => names.includes(r.role_name));

  // maxRoleLevel proviene de get_user_max_level (computado en BD sobre los
  // mismos user_roles activos): es la misma fuente unica por otra proyeccion.
  // Cubre el caso de get_user_roles colgado/timeout donde el nivel SI llego.
  const isSuperAdmin = hasActiveRole([ROLES.SUPER_ADMIN]) || maxRoleLevel >= 5;
  const isAdmin = isSuperAdmin || hasActiveRole([ROLES.ADMIN]) || maxRoleLevel >= 4;
  const isDriver = hasActiveRole([ROLES.DRIVER, 'drivers']);
  const isPassenger = hasActiveRole([ROLES.PASSENGER]);
  // Driver con rol INACTIVO: registrado pero pendiente de aprobacion del
  // super admin. NO opera como conductor; solo la UI usa esto para mostrar
  // el estado "Registro en espera de activacion" (DriverView pendiente).
  const hasPendingDriverRole = !isDriver && userRoles.some(r =>
    (r.role_name === ROLES.DRIVER || r.role_name === 'drivers') && r.is_active === false
  );

  const highestActiveRole = activeRoles.length > 0
    ? activeRoles.reduce((a, b) => a.level > b.level ? a : b)
    : null;
  const role = highestActiveRole?.role_name || (isSuperAdmin ? 'super_admin' : (isAdmin ? 'admin' : (isDriver ? 'driver' : (isPassenger ? 'passenger' : null))));
  const logout = React.useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('Error en supabase.auth.signOut:', err);
    }
    setCurrentUser(null);
    setProfile(null);
    setUserRoles([]);
    setMaxRoleLevel(0);
    setIsVip(false);
    writeAuthCache(null);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  }, []);

  const value = React.useMemo(() => ({
    currentUser,
    setCurrentUser,
    profile,
    userRoles,
    maxRoleLevel,
    loading,
    role,
    isDriver,
    isAdmin,
    isPassenger,
    isSuperAdmin,
    hasPendingDriverRole,
    isVip,
    logout,
  }), [
    currentUser,
    profile,
    userRoles,
    maxRoleLevel,
    loading,
    role,
    isDriver,
    isAdmin,
    isPassenger,
    isSuperAdmin,
    hasPendingDriverRole,
    isVip,
    logout,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
